import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShieldCheck, ShoppingBag, Truck } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, eur, img, LINES, NEEDS } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PackPicker } from "@/components/PackPicker";
import { SizeGuide } from "@/components/SizeGuide";
import { DoseByWeight } from "@/components/DoseByWeight";
import { ProductFit } from "@/components/ProductFit";

export default function ProductPage() {
  const { slug } = useParams();
  const { add, settings } = useCart();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  const [fmt, setFmt] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setP(null); setErr(false); setImgIdx(0); setQty(1);
    api.get(`/products/${slug}`).then((r) => { setP(r.data); setFmt(r.data.formats[0]); setImgIdx(r.data.format_images?.[r.data.formats[0].id] ?? 0); }).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="max-w-3xl mx-auto px-4 py-24 text-center"><h1 className="font-display text-4xl font-black text-[#0B1D3A]">Producto no encontrado</h1><Link to="/tienda" className="text-[#DC2626] font-bold mt-4 inline-block">Volver a la tienda</Link></div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10"><div className="aspect-square bg-slate-100 animate-pulse rounded" /><div className="h-96 bg-slate-100 animate-pulse rounded" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-10" data-testid="product-page">
      <nav className="text-sm text-slate-500 mb-6"><Link to="/tienda" className="hover:text-[#DC2626]">Tienda</Link> / <Link to={`/tienda?line=${p.line}`} className="hover:text-[#DC2626]">{LINES[p.line]}</Link></nav>
      <div className="grid md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="bg-gradient-to-b from-[#EFF6FF] to-white border border-slate-200 rounded aspect-square grid place-items-center overflow-hidden">
            <img src={img(p.images[imgIdx])} alt={p.name} className="w-full h-full object-contain p-8 mix-blend-multiply" data-testid="product-main-image" />
          </div>
          {p.images.length > 1 && (
            <div className="flex gap-3 mt-3">
              {p.images.map((u, i) => (
                <button key={u} onClick={() => setImgIdx(i)} data-testid={`product-thumb-${i}`}
                  className={`w-20 h-20 border-2 rounded overflow-hidden bg-white ${i === imgIdx ? "border-[#DC2626]" : "border-slate-200"}`}>
                  <img src={img(u)} alt="" className="w-full h-full object-contain p-1 mix-blend-multiply" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <span className="text-[11px] font-bold tracking-wider uppercase bg-[#0B1D3A] text-white px-2 py-1 rounded-sm">{LINES[p.line]}</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-[#0B1D3A] mt-4 leading-none" data-testid="product-name">{p.name}</h1>
          <div className="flex flex-wrap gap-1.5 mt-4">
            {p.needs.map((n) => <Link key={n} to={`/tienda?need=${n}`} className="text-xs bg-[#EFF6FF] text-[#1E3A8A] font-semibold px-2.5 py-1 rounded-full">{NEEDS[n]}</Link>)}
          </div>
          <p className="text-slate-700 mt-5 leading-relaxed whitespace-pre-line" data-testid="product-short">{p.short}</p>

          {p.bundle ? <PackPicker p={p} /> : (<>
          <div className="flex items-center justify-between gap-4 mt-7">
            <p className="font-display font-bold text-[#0B1D3A] text-lg">{p.line === "gear" ? "Talla" : "Formato"}</p>
            {p.line === "gear" && p.formats.length > 1 && <SizeGuide />}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {p.formats.map((f) => (
              <button key={f.id} onClick={() => { setFmt(f); if (p.format_images?.[f.id] !== undefined) setImgIdx(p.format_images[f.id]); }} data-testid={`format-option-${f.id}`}
                className={`px-4 py-2.5 rounded border-2 text-sm font-semibold transition-colors ${fmt.id === f.id ? "border-[#0B1D3A] bg-[#0B1D3A] text-white" : "border-slate-200 hover:border-[#2563EB]"}`}>
                {f.label} <span className={fmt.id === f.id ? "text-blue-200" : "text-[#DC2626]"}>· {eur(f.price)}</span>
              </button>
            ))}
          </div>
          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-4xl font-extrabold text-[#DC2626]" data-testid="product-price">{eur(fmt.price)}</span>
            <span className="text-sm text-slate-500">IVA incluido</span>
          </div>
          <div className="flex gap-3 mt-6">
            <div className="flex items-center border-2 border-slate-200 rounded-full">
              <button className="p-3" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="product-qty-dec"><Minus size={16} /></button>
              <span className="w-8 text-center font-bold" data-testid="product-qty">{qty}</span>
              <button className="p-3" onClick={() => setQty(Math.min(99, qty + 1))} data-testid="product-qty-inc"><Plus size={16} /></button>
            </div>
            <button onClick={() => add(p, fmt, qty)} data-testid="product-add-to-cart"
              className="flex-1 flex items-center justify-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] disabled:bg-slate-300 text-white font-display font-bold text-lg rounded-full transition-colors">
              <ShoppingBag size={19} /> Añadir al carrito
            </button>
          </div>
          </>)}
          {p.dose_calc?.length > 0 && <DoseByWeight steps={p.dose_calc} />}
          {p.line !== "gear" && <ProductFit p={p} />}
          <div className="mt-6 grid grid-cols-2 gap-3 text-sm text-slate-600">
            <div className="flex items-center gap-2"><Truck size={18} className="text-[#2563EB]" /> Envío gratis desde {Number(settings.free_shipping_from).toLocaleString("es-ES")} €</div>
            <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#2563EB]" /> Pago seguro Redsys</div>
          </div>
          {settings.discount_tiers?.length > 0 && (
            <p className="mt-6 text-sm border-2 border-[#DC2626]/30 text-[#0B1D3A] p-3 rounded" data-testid="product-volume-discounts">
              <b className="text-[#DC2626]">Descuentos por volumen:</b> {settings.discount_tiers.map((t) => `${t.pct} % desde ${t.min} €`).join(" · ")}
            </p>
          )}
          {p.dose && <p className="mt-3 text-sm bg-[#EFF6FF] text-[#0B1D3A] p-3 rounded"><strong>Dosis orientativa:</strong> {p.dose}</p>}
        </div>
      </div>

      <Tabs defaultValue="desc" className="mt-14">
        <TabsList className="bg-transparent border-b border-slate-200 w-full justify-start rounded-none h-auto p-0 gap-6">
          {[["desc", "Descripción"], ["comp", p.line === "gear" ? "Materiales" : "Composición"], ["uso", p.line === "gear" ? "Cuidados" : "Modo de empleo"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`product-tab-${v}`}
              className="font-display text-lg font-bold rounded-none px-0 pb-3 data-[state=active]:shadow-none data-[state=active]:text-[#DC2626] data-[state=active]:border-b-2 data-[state=active]:border-[#DC2626]">
              {l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="desc" className="max-w-3xl text-slate-700 leading-relaxed pt-4 whitespace-pre-line" data-testid="product-tab-content-desc">{p.description}</TabsContent>
        <TabsContent value="comp" className="max-w-3xl text-slate-700 leading-relaxed pt-4 whitespace-pre-line" data-testid="product-tab-content-comp">{p.composition}</TabsContent>
        <TabsContent value="uso" className="max-w-3xl text-slate-700 leading-relaxed pt-4 whitespace-pre-line" data-testid="product-tab-content-uso">
          {p.usage}
          {p.line !== "gear" && <p className="text-xs text-slate-500 mt-4">Pienso complementario. Consulte con su veterinario para adaptar la suplementación a las necesidades de su caballo.</p>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
